<div class="page">
  <nav class="breadcrumb" [attr.aria-label]="'COMMON.BREADCRUMB' | translate">
    <a [routerLink]="usersLink" class="breadcrumb-link">{{ 'USERS.TITLE' | translate }}</a>
    <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
    <span class="breadcrumb-current">{{ user?.username || '...' }}</span>
  </nav>

  <div class="page-header">
    <div>
      <h1>{{ displayName || ('USERS.PROFILE' | translate) }}</h1>
      <p class="page-subtitle" *ngIf="user">{{ '@' + user.username }}</p>
    </div>
    <div class="page-actions" *ngIf="user">
      <button type="button" class="btn btn-secondary" (click)="goBack()">
        <mat-icon>arrow_back</mat-icon>
        {{ 'COMMON.BACK' | translate }}
      </button>
      <button type="button" class="btn btn-primary" *ngIf="permissions.canEdit('users')" (click)="onEdit()">
        <mat-icon>edit</mat-icon>
        {{ 'COMMON.EDIT' | translate }}
      </button>
    </div>
  </div>

  <div class="alert alert-error" *ngIf="errorMessage">{{ errorMessage | translate }}</div>

  <div class="card detail-card" *ngIf="loading">
    <div class="empty-state">{{ 'USERS.LOADING_PROFILE' | translate }}</div>
  </div>

  <div class="user-profile" *ngIf="!loading && user">
    <section class="profile-hero card">
      <div class="profile-avatar">{{ initials }}</div>
      <div class="profile-summary">
        <h2>{{ displayName }}</h2>
        <p class="profile-username">{{ '@' + user.username }}</p>
        <div class="profile-badges">
          <span class="role-badge" *ngIf="user.role?.name">
            <mat-icon>badge</mat-icon>
            {{ user.role.name }}
          </span>
          <span class="default-badge" *ngIf="isSystemUser">
            <mat-icon>verified</mat-icon>
            {{ 'USERS.SYSTEM_ACCOUNT' | translate }}
          </span>
        </div>
      </div>
    </section>

    <nav class="profile-tabs" role="tablist" [attr.aria-label]="'USERS.PROFILE' | translate">
      <button
        type="button"
        class="profile-tab"
        *ngFor="let tab of tabs"
        role="tab"
        [id]="'user-tab-' + tab.id"
        [attr.aria-selected]="activeTab === tab.id"
        [attr.aria-controls]="'user-panel-' + tab.id"
        [class.active]="activeTab === tab.id"
        (click)="setTab(tab.id)"
      >
        <mat-icon>{{ tab.icon }}</mat-icon>
        {{ tab.labelKey | translate }}
        <span class="tab-count" *ngIf="tab.id === 'access'">{{ grantedAccessCount }}</span>
      </button>
    </nav>

    <div
      class="tab-panel"
      id="user-panel-overview"
      role="tabpanel"
      aria-labelledby="user-tab-overview"
      *ngIf="activeTab === 'overview'"
    >
      <div class="profile-sections">
        <section class="info-card card">
          <header class="section-header">
            <mat-icon>person</mat-icon>
            <h3>{{ 'USERS.ACCOUNT' | translate }}</h3>
          </header>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">{{ 'USERS.FULL_NAME' | translate }}</span>
              <span class="info-value">{{ displayName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ 'USERS.USERNAME' | translate }}</span>
              <span class="info-value">{{ user.username }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ 'USERS.ROLE' | translate }}</span>
              <span class="info-value">{{ user.role?.name || ('COMMON.NOT_ASSIGNED' | translate) }}</span>
            </div>
            <div class="info-row" *ngIf="isSystemUser">
              <span class="info-label">{{ 'USERS.ACCOUNT_TYPE' | translate }}</span>
              <span class="info-value">{{ 'USERS.SYSTEM_USER' | translate }}</span>
            </div>
          </div>
        </section>

        <section class="info-card card" *ngIf="!isSystemUser">
          <header class="section-header">
            <mat-icon>class</mat-icon>
            <h3>{{ 'USERS.CLASSES' | translate }}</h3>
            <span class="section-count" *ngIf="user.classes?.length">{{ user.classes.length }}</span>
          </header>

          <div class="assignment-groups" *ngIf="classGroups.length; else noClasses">
            <div class="assignment-group" *ngFor="let group of classGroups">
              <div class="assignment-group-title">{{ group.majorName }}</div>
              <div class="chip-list">
                <span class="chip chip-class" *ngFor="let cls of group.classes">{{ cls.name }}</span>
              </div>
            </div>
          </div>
          <ng-template #noClasses>
            <p class="empty-assignment">{{ 'USERS.NO_CLASSES' | translate }}</p>
          </ng-template>
        </section>

        <section class="info-card card" *ngIf="!isSystemUser">
          <header class="section-header">
            <mat-icon>menu_book</mat-icon>
            <h3>{{ 'USERS.SUBJECTS' | translate }}</h3>
            <span class="section-count" *ngIf="user.subjects?.length">{{ user.subjects.length }}</span>
          </header>

          <div class="chip-list" *ngIf="user.subjects?.length; else noSubjects">
            <span class="chip chip-subject" *ngFor="let subject of user.subjects">{{ subject.name }}</span>
          </div>
          <ng-template #noSubjects>
            <p class="empty-assignment">{{ 'USERS.NO_SUBJECTS' | translate }}</p>
          </ng-template>
        </section>
      </div>
    </div>

    <div
      class="tab-panel access-panel"
      id="user-panel-access"
      role="tabpanel"
      aria-labelledby="user-tab-access"
      *ngIf="activeTab === 'access'"
    >
      <section class="access-panel-inner">
        <div class="access-toolbar">
          <div class="access-meta">
            <div class="access-meta-item">
              <span class="info-label">{{ 'USERS.PORTAL' | translate }}</span>
              <span class="portal-pill" [attr.data-portal]="userPortal">{{ portalLabelKey | translate }}</span>
            </div>
            <p class="access-note">{{ 'USERS.ACCESS_EDIT_HINT' | translate }}</p>
            <p class="access-dirty" *ngIf="accessDirty">{{ 'USERS.ACCESS_UNSAVED' | translate }}</p>
          </div>
          <div class="access-actions" *ngIf="permissions.canEdit('users')">
            <button type="button" class="btn btn-secondary" (click)="askResetAccess()" [disabled]="savingAccess || (!hasCustomAccess && !accessDirty)">
              {{ 'USERS.ACCESS_RESET' | translate }}
            </button>
            <button type="button" class="btn btn-primary" (click)="saveAccess()" [disabled]="savingAccess || !accessDirty">
              {{ (savingAccess ? 'COMMON.SAVING' : 'COMMON.SAVE') | translate }}
            </button>
          </div>
        </div>

        <div class="alert alert-error" *ngIf="accessError">{{ accessError | translate }}</div>

        <div class="matrix-wrap">
          <div class="assignment-group-title">{{ 'USERS.ACCESS_MATRIX' | translate }}</div>
          <div class="matrix-scroll">
            <table class="access-matrix">
              <thead>
                <tr>
                  <th scope="col">{{ 'USERS.SIDEBAR_ITEM' | translate }}</th>
                  <th scope="col" class="is-user-portal" *ngFor="let action of accessActions">
                    {{ actionLabel(action) | translate }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let group of accessMatrixGroups">
                  <tr class="matrix-group-row">
                    <td [attr.colspan]="1 + accessActions.length">{{ group.label | translate }}</td>
                  </tr>
                  <tr *ngFor="let item of group.items">
                    <td>
                      <span class="matrix-feature">
                        <mat-icon>{{ item.icon }}</mat-icon>
                        {{ item.label | translate }}
                      </span>
                    </td>
                    <td
                      *ngFor="let action of accessActions"
                      class="matrix-cell is-user-portal"
                      [class.has-access]="supportsAction(item, action) && isGranted(item, action)"
                      [class.is-muted]="supportsAction(item, action) && action !== 'view' && !isGranted(item, 'view')"
                    >
                      <button
                        *ngIf="supportsAction(item, action)"
                        type="button"
                        class="access-toggle"
                        [class.on]="isGranted(item, action)"
                        [disabled]="savingAccess || isAccessLocked(item) || !permissions.canEdit('users')"
                        (click)="toggleAccess(item, action)"
                        [attr.aria-pressed]="isGranted(item, action)"
                        [attr.aria-label]="(item.label | translate) + ' — ' + (actionLabel(action) | translate)"
                      >
                        <mat-icon>{{ isGranted(item, action) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
                      </button>
                    </td>
                  </tr>
                </ng-container>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </div>
</div>

<app-users-add
  [(open)]="open"
  [(mode)]="mode"
  [(id)]="selectedIdForEdit"
  (userUpdated)="handleUserUpdated()"
></app-users-add>

<div class="modal-backdrop" *ngIf="showResetConfirm">
  <div class="modal-panel" role="dialog" aria-modal="true" [attr.aria-label]="'USERS.ACCESS_RESET_TITLE' | translate">
    <div class="modal-header">
      <h3>{{ 'USERS.ACCESS_RESET_TITLE' | translate }}</h3>
    </div>
    <div class="modal-body">
      <p>{{ 'USERS.ACCESS_RESET_BODY' | translate }}</p>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="showResetConfirm = false" [disabled]="savingAccess">
        {{ 'COMMON.CANCEL' | translate }}
      </button>
      <button type="button" class="btn btn-primary" (click)="confirmResetAccess()" [disabled]="savingAccess">
        {{ 'USERS.ACCESS_RESET' | translate }}
      </button>
    </div>
  </div>
</div>
